import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';

import './SalespersonAdmin.css';
import {
  Row,
  Col,
  Card,
  Form,
  Input,
  Button,
  Table,
  Space,
  Typography,
  Modal,
  message,
  Tooltip,
  Switch,
  Spin,
  Select
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  ExclamationCircleOutlined,
  SaveOutlined
} from '@ant-design/icons';
import moment from 'moment';

const { Text } = Typography;
const { confirm } = Modal;

const SalespersonAdmin = () => {
  const [form] = Form.useForm();
  const [users, setUsers] = useState([]);
  const [salespersons, setSalespersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const API_URL = process.env.REACT_APP_API_URL;



  const fetchSalespersons = useCallback(async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API_URL}/sales-persons`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setSalespersons(response.data);
    setLoading(false);
  } catch (error) {
    console.error('❌ Error fetching salespersons:', error);
    if (error.response) {
      console.error("Server responded with:", error.response.data);
    }
    message.error('Failed to fetch salespersons. Please try again later.');
    setLoading(false);
  }
}, [API_URL]);

const fetchUsers = useCallback(async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API_URL}/users/sales-person`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setUsers(response.data);
  } catch (error) {
    console.error('❌ Error fetching users:', error);
    if (error.response) {
      console.error("Server responded with:", error.response.data);
    }
    message.error('Failed to fetch users. Please try again later.');
  }
}, [API_URL]);

useEffect(() => {
  fetchSalespersons();
  fetchUsers();
}, [fetchSalespersons, fetchUsers]);


const handleSave = async (values) => {
  setSubmitting(true);
  const token = localStorage.getItem('token');

  try {
    // If user selected a linked user, include it
    const payload = {
      full_name: values.full_name,
      nickname: values.nickname || null,
      phone: values.phone || null,
      email: values.email || null,
      user_id: values.user_id || null, // optional
      is_active: values.is_active ?? true
    };

    let response;
    if (editRecord) {
      response = await axios.put(`${API_URL}/sales-persons/${editRecord.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSalespersons(prev =>
        prev.map(s => (s.id === response.data.id ? response.data : s))
      );
      message.success('Salesperson updated successfully');
    } else {
      response = await axios.post(`${API_URL}/sales-persons`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSalespersons(prev => [response.data, ...prev]);
      message.success('Salesperson created successfully');
    }

    setModalVisible(false);
    form.resetFields();
    setEditRecord(null);
  } catch (error) {
    console.error('Error saving salesperson:', error);
    message.error('Failed to save salesperson. Please try again.');
  } finally {
    setSubmitting(false);
  }
};


  const handleEdit = (record) => {
    setEditRecord(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API_URL}/sales-persons/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSalespersons(prev => prev.filter(s => s.id !== id));
      message.success('Salesperson deleted successfully');
    } catch (error) {
      console.error('Error deleting salesperson:', error);
      message.error('Failed to delete salesperson');
    }
  };

  const showDeleteConfirm = (id, name) => {
    confirm({
      title: 'Are you sure you want to delete this salesperson?',
      content: `Salesperson: "${name}"`,
      icon: <ExclamationCircleOutlined />,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        handleDelete(id);
      }
    });
  };

  const columns = [
    {
      title: 'Full Name',
      dataIndex: 'full_name',
      key: 'full_name',
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: 'Nickname',
      dataIndex: 'nickname',
      key: 'nickname'
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone'
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email'
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active) => (
        <Switch checked={active} disabled />
      )
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => moment(text).format('DD MMM YYYY')
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit">
            <Button
              type="primary"
              shape="circle"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="primary"
              shape="circle"
              danger
              icon={<DeleteOutlined />}
              onClick={() => showDeleteConfirm(record.id, record.full_name)}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Row gutter={[24, 24]}>
        <Col xs={24}>
          <Card
            title="Salespersons"
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  form.resetFields();
                  setEditRecord(null);
                  setModalVisible(true);
                }}
              >
                Add Salesperson
              </Button>
            }
          >
            <Table
              columns={columns}
              dataSource={salespersons}
              rowKey="id"
              loading={loading}
              pagination={{
                pageSize: 7,
                showSizeChanger: false,
                showTotal: (total) => `Total ${total} salespersons`
              }}
            />
          </Card>
        </Col>
      </Row>

      <Modal
        title={editRecord ? 'Edit Salesperson' : 'Add Salesperson'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditRecord(null);
          form.resetFields();
        }}
        footer={null}
      >
        <Spin spinning={submitting}>
          <Form form={form} layout="vertical" onFinish={handleSave}>
            <Form.Item name="user_id" label="Linked User">
                <Select
                    allowClear
                    placeholder="Select user (optional)"
                    optionFilterProp="children"
                    showSearch
                    onChange={(value) => {
                    const selectedUser = users.find(u => u.userid === value);
                    if (selectedUser) {
                        form.setFieldsValue({
                        full_name: `${selectedUser.firstname} ${selectedUser.lastname}`,
                        nickname: selectedUser.nickname || '',
                        email: selectedUser.email || '',
                        phone: selectedUser.phone || ''
                        });
                    } else {
                        // Reset fields if cleared
                        form.setFieldsValue({
                        full_name: '',
                        nickname: '',
                        email: '',
                        phone: ''
                        });
                    }
                    }}
                >
                    {users.map((u) => (
                    <Select.Option key={u.userid} value={u.userid}>
                        {u.firstname} {u.lastname} ({u.role})
                    </Select.Option>
                    ))}
                </Select>
                </Form.Item>

            <Form.Item
              name="full_name"
              label="Full Name"
              rules={[{ required: true, message: 'Please enter full name' }]}
            >
              <Input placeholder="Enter full name" />
            </Form.Item>

            <Form.Item name="nickname" label="Nickname">
              <Input placeholder="Enter nickname" />
            </Form.Item>

            <Form.Item name="phone" label="Phone">
              <Input placeholder="Enter phone number" />
            </Form.Item>

            <Form.Item name="email" label="Email">
              <Input type="email" placeholder="Enter email address" />
            </Form.Item>

            <Form.Item name="is_active" label="Active" valuePropName="checked">
              <Switch defaultChecked />
            </Form.Item>

            <Form.Item>
              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button onClick={() => setModalVisible(false)}>Cancel</Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={submitting}
                >
                  {editRecord ? 'Update' : 'Create'}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>
    </div>
  );
};

export default withRoleAccess(SalespersonAdmin, ['superadmin']);
